import { storageKey, writeLocalJson, removeLocalKey, readLocalJsonOptional } from '@authe/fe-commons/storage';
import { http, fallback, defineChain, numberToHex, recoverMessageAddress, isAddressEqual, verifyMessage as verifyMessage$1, createPublicClient } from 'viem';
export { getAddress, isAddress, isAddressEqual } from 'viem';
import { ChainFamily, registry, isActiveChain, getChainMetadata, toHexChainId, normalizeAddress, chainSelectOptions } from '@authe/fe-commons/chains';
import { WagmiProvider, createConfig, injected, usePublicClient, useWalletClient, useSignMessage, useAccount, useChainId, useChains, useSwitchChain, useConnect, useDisconnect } from 'wagmi';
import { createLogger } from '@authe/fe-commons/logging';
import { blockArg } from '@authe/fe-commons/contracts';
import * as React10 from 'react';
import { TwoKeySigner } from '@authe/fe-commons/signing';
import { verifyMessage } from 'viem/actions';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { jsx, jsxs } from 'react/jsx-runtime';
import { useChainRegistryVersion, Select, SelectTrigger, SelectValue, cn, SelectContent, SelectItem, ChainAvatar, Badge, AddressDisplay, Button } from '@authe/ui-core';

// src/config.ts
var WEB3_PACKAGE = "web3";
var WEB3_SELECTED_CHAIN_KEY = storageKey(WEB3_PACKAGE, "chain");
var WEB3_EVM_CONNECTION_ID = "evm:wagmi";
var WEB3_DEFAULT_CHAIN_SLUG = "eth";
var WEB3_SIGNER_TTL_MS = 15 * 6e4;
var WEB3_SIGNER_ROOT_ID = "authe-web3";
var FLAGS_KEY = storageKey(WEB3_PACKAGE, "disconnected");
function readFlags() {
  return readLocalJsonOptional(FLAGS_KEY) ?? {};
}
function markDisconnected(id) {
  const flags = readFlags();
  if (flags[id]) return;
  flags[id] = true;
  writeLocalJson(FLAGS_KEY, flags);
}
function clearDisconnected(id) {
  const flags = readFlags();
  if (!flags[id]) return;
  delete flags[id];
  if (Object.keys(flags).length === 0) removeLocalKey(FLAGS_KEY);
  else writeLocalJson(FLAGS_KEY, flags);
}
function isDisconnected(id) {
  return readFlags()[id] === true;
}
function resolveRpcUrls(chain, overrides) {
  if (overrides) {
    if (chain.chainIdHex && overrides[chain.chainIdHex]?.length) return overrides[chain.chainIdHex];
    if (overrides[chain.slug]?.length) return overrides[chain.slug];
  }
  return [...chain.rpcUrls ?? []];
}
function rpcTransport(urls) {
  if (urls.length <= 1) return http(urls[0]);
  return fallback(urls.map((url) => http(url)));
}
function toViemChain(chain, options = {}) {
  if (chain.family !== ChainFamily.EVM || !chain.chainIdHex) return null;
  let id;
  try {
    id = Number(BigInt(chain.chainIdHex));
  } catch {
    return null;
  }
  if (!Number.isSafeInteger(id) || id <= 0) return null;
  const urls = options.rpcUrls ?? resolveRpcUrls(chain, options.overrides);
  if (!urls.length) return null;
  return defineChain({
    id,
    name: chain.name,
    nativeCurrency: { name: chain.nativeToken, symbol: chain.nativeToken, decimals: chain.decimals },
    rpcUrls: { default: { http: urls } },
    ...chain.explorerUrl ? { blockExplorers: { default: { name: `${chain.name} Explorer`, url: chain.explorerUrl } } } : {},
    testnet: chain.isTestnet
  });
}
function autheEvmViemChains(options = {}) {
  const wanted = options.slugs ? new Set(options.slugs) : null;
  const out = [];
  for (const chain of registry.all()) {
    if (chain.family !== ChainFamily.EVM) continue;
    if (!isActiveChain(chain)) continue;
    if (!options.includeTestnets && chain.isTestnet) continue;
    if (wanted && !wanted.has(chain.slug)) continue;
    const viem = toViemChain(chain, { overrides: options.overrides });
    if (!viem) continue;
    if (chain.slug === WEB3_DEFAULT_CHAIN_SLUG) out.unshift(viem);
    else out.push(viem);
  }
  return out;
}
var log = createLogger(`${WEB3_PACKAGE}:wagmi`);
function createAutheWagmiConfig(options = {}) {
  const resolved = options.chains ?? autheEvmViemChains({
    includeTestnets: options.includeTestnets,
    overrides: options.rpc,
    slugs: options.slugs
  });
  if (resolved.length === 0) {
    throw new Error(
      "createAutheWagmiConfig: no EVM chains resolved from the registry \u2014 supply `rpc` overrides or explicit `chains`."
    );
  }
  const chains = resolved;
  const transports = Object.fromEntries(
    chains.map((c) => [c.id, options.transports?.[c.id] ?? rpcTransport(c.rpcUrls.default.http)])
  );
  log.debug("creating wagmi config", { chains: chains.map((c) => c.id) });
  return createConfig({
    chains,
    connectors: options.connectors ?? [injected()],
    transports,
    ssr: options.ssr
  });
}

// src/contract/keys.ts
function stableArgsKey(args) {
  function encode(value) {
    if (value === null) return ["null"];
    if (Array.isArray(value)) return ["array", value.map(encode)];
    if (typeof value === "object") {
      return ["object", Object.keys(value).sort().map((key) => [key, encode(value[key])])];
    }
    return [typeof value, String(value)];
  }
  return JSON.stringify(encode(args ?? []));
}
var DEFAULT_LOG_WINDOW = 1000n;
function toError(reason) {
  return reason instanceof Error ? reason : new Error(String(reason));
}
function createRpcContractReader(options = {}) {
  const clients = /* @__PURE__ */ new Map();
  function clientFor(chainId) {
    const key = chainId.toLowerCase();
    const cached = clients.get(key);
    if (cached) return cached;
    const meta = getChainMetadata(chainId);
    if (!meta) throw new Error(`Unknown chain: ${chainId}`);
    const viemChain = toViemChain(meta, { overrides: options.rpcOverrides });
    if (!viemChain) throw new Error(`No RPC endpoint for chain ${meta.slug} (${chainId})`);
    const client = createPublicClient({
      chain: viemChain,
      transport: rpcTransport(viemChain.rpcUrls.default.http)
    });
    clients.set(key, client);
    return client;
  }
  return {
    id: "rpc",
    async read(ref, req) {
      const client = clientFor(ref.chainId);
      return client.readContract({
        address: ref.address,
        abi: req.abi,
        functionName: req.functionName,
        args: req.args,
        ...blockArg(ref.block)
      });
    },
    async readMany(ref, reqs) {
      if (reqs.length === 0) return [];
      const client = clientFor(ref.chainId);
      const settled = await Promise.allSettled(
        reqs.map(
          (r) => client.readContract({
            address: ref.address,
            abi: r.abi,
            functionName: r.functionName,
            args: r.args,
            ...blockArg(ref.block)
          })
        )
      );
      return settled.map(
        (s) => s.status === "fulfilled" ? { status: "success", data: s.value } : { status: "failure", error: toError(s.reason) }
      );
    },
    async call(ref, data) {
      const client = clientFor(ref.chainId);
      const result = await client.call({ to: ref.address, data, ...blockArg(ref.block) });
      return result.data ?? "0x";
    },
    async getStorageAt(ref, slot) {
      const client = clientFor(ref.chainId);
      return client.getStorageAt({ address: ref.address, slot, ...blockArg(ref.block) });
    },
    async resolveBlockNumber(ref) {
      if (ref.block?.blockNumber != null) return ref.block.blockNumber;
      if (ref.block?.blockTag === "pending") throw new Error("Choose a mined block to inspect proxy storage.");
      const block = await clientFor(ref.chainId).getBlock({ blockTag: ref.block?.blockTag ?? "latest" });
      if (block.number == null) throw new Error("The selected block has not been mined.");
      return block.number;
    },
    async getBytecode(ref) {
      return clientFor(ref.chainId).getBytecode({ address: ref.address, ...blockArg(ref.block) });
    },
    async getLogs(ref, query = {}) {
      const client = clientFor(ref.chainId);
      const window2 = query.lookback ?? options.logWindow ?? DEFAULT_LOG_WINDOW;
      const toBlock = query.toBlock ?? ref.block?.blockNumber ?? ref.block?.blockTag ?? "latest";
      let fromBlock = query.fromBlock;
      if (fromBlock === void 0) {
        const head = typeof toBlock === "bigint" ? toBlock : toBlock === "earliest" ? 0n : toBlock === "latest" || toBlock === "pending" ? await client.getBlockNumber() : (await client.getBlock({ blockTag: toBlock })).number;
        fromBlock = head > window2 ? head - window2 : 0n;
      }
      if (typeof fromBlock === "bigint" && fromBlock < 0n || typeof toBlock === "bigint" && toBlock < 0n) {
        throw new Error("Block heights must be non-negative.");
      }
      if (typeof fromBlock === "bigint" && typeof toBlock === "bigint" && fromBlock > toBlock) {
        throw new Error("From block must not be after To block.");
      }
      if (query.topics?.length) {
        const logs2 = await client.request({ method: "eth_getLogs", params: [{
          address: ref.address,
          fromBlock: typeof fromBlock === "bigint" ? numberToHex(fromBlock) : fromBlock,
          toBlock: typeof toBlock === "bigint" ? numberToHex(toBlock) : toBlock,
          topics: [query.topics]
        }] });
        return logs2.map((log7) => ({
          address: log7.address,
          topics: log7.topics,
          data: log7.data,
          blockNumber: log7.blockNumber == null ? null : BigInt(log7.blockNumber),
          logIndex: log7.logIndex == null ? null : Number(BigInt(log7.logIndex)),
          transactionHash: log7.transactionHash
        }));
      }
      const logs = await client.getLogs({ address: ref.address, fromBlock, toBlock });
      return logs.map((log7) => ({
        address: log7.address,
        topics: log7.topics,
        data: log7.data,
        blockNumber: log7.blockNumber,
        logIndex: log7.logIndex,
        transactionHash: log7.transactionHash
      }));
    }
  };
}
function blockOverride(selector) {
  if (selector.blockNumber != null) return { blockNumber: selector.blockNumber };
  if (selector.blockTag) return { blockTag: selector.blockTag };
  return {};
}
function useEvmRead(params) {
  const { chainId, blockNumber, blockTag, ready, deps, read, onFailure } = params;
  const client = usePublicClient(chainId != null ? { chainId } : void 0);
  const [nonce, setNonce] = React10.useState(0);
  const enabled = ready && Boolean(client);
  const depsKey = stableArgsKey(deps);
  const request = React10.useMemo(
    () => ({ enabled, client, chainId, blockNumber, blockTag, nonce, depsKey }),
    [enabled, client, chainId, blockNumber, blockTag, nonce, depsKey]
  );
  const [result, setResult] = React10.useState(null);
  React10.useEffect(() => {
    if (!enabled || !client) return void 0;
    let cancelled = false;
    Promise.resolve().then(() => read(client, blockOverride({ blockNumber, blockTag }))).then((result2) => {
      if (cancelled) return;
      setResult({ request, data: result2, error: null });
    }).catch((err) => {
      if (cancelled) return;
      if (onFailure) {
        onFailure.log.warn(onFailure.message, { ...onFailure.context?.(), error: String(err) });
      }
      setResult({ request, error: err instanceof Error ? err : new Error(String(err)) });
    });
    return () => {
      cancelled = true;
    };
  }, [request]);
  const refetch = React10.useCallback(() => setNonce((n) => n + 1), []);
  const current = enabled && result?.request === request ? result : null;
  return { data: current?.data, error: current?.error ?? null, isPending: enabled && !current, refetch };
}
var log2 = createLogger(`${WEB3_PACKAGE}:contract-read`);
function useContractRead(params) {
  const { address, abi, functionName, args, chainId, blockNumber, blockTag, enabled = true } = params;
  const argsKey = stableArgsKey(args);
  return useEvmRead({
    chainId,
    blockNumber,
    blockTag,
    ready: enabled && Boolean(address && abi && functionName),
    deps: [address, functionName, argsKey, stableArgsKey(abi)],
    read: async (client, block) => await client.readContract({
      address,
      abi,
      functionName,
      args,
      ...block
    }),
    onFailure: { log: log2, message: "contract read failed", context: () => ({ address, functionName }) }
  });
}
var log3 = createLogger(`${WEB3_PACKAGE}:contract-reads`);
function useContractReads(params) {
  const { calls, chainId, blockNumber, blockTag, enabled = true } = params;
  const callsKey = React10.useMemo(
    () => stableArgsKey(calls),
    [calls]
  );
  const { data, isPending, refetch } = useEvmRead({
    chainId,
    blockNumber,
    blockTag,
    ready: enabled && calls.length > 0,
    deps: [callsKey],
    read: async (client, block) => {
      const settled = await Promise.allSettled(
        calls.map(
          (c) => client.readContract({
            address: c.address,
            abi: c.abi,
            functionName: c.functionName,
            args: c.args,
            ...block
          })
        )
      );
      return settled.map(
        (s) => s.status === "fulfilled" ? { status: "success", data: s.value } : { status: "failure", error: s.reason instanceof Error ? s.reason : new Error(String(s.reason)) }
      );
    },
    onFailure: { log: log3, message: "batch read failed" }
  });
  return { results: data ?? [], isPending, refetch };
}
var log4 = createLogger(`${WEB3_PACKAGE}:contract-write`);
function writeOutcome(receipt) {
  if (!receipt) return "sent";
  return receipt.status === "reverted" ? "reverted" : "success";
}
function useContractWrite(chainId) {
  const { data: walletClient, error: walletError } = useWalletClient(chainId != null ? { chainId } : void 0);
  const publicClient = usePublicClient(chainId != null ? { chainId } : void 0);
  const [hash, setHash] = React10.useState(null);
  const [receipt, setReceipt] = React10.useState(null);
  const [status, setStatus] = React10.useState("idle");
  const [error, setError] = React10.useState(null);
  const attemptRef = React10.useRef(0);
  React10.useEffect(() => () => {
    attemptRef.current += 1;
  }, []);
  const reset = React10.useCallback(() => {
    attemptRef.current += 1;
    setHash(null);
    setReceipt(null);
    setStatus("idle");
    setError(null);
  }, []);
  const write = React10.useCallback(
    async (call, value) => {
      const attempt = ++attemptRef.current;
      const current = () => attempt === attemptRef.current;
      let submittedHash = null;
      setHash(null);
      setReceipt(null);
      setError(null);
      if (!walletClient) {
        const err = walletError ?? new Error("No connected wallet to sign the transaction.");
        setError(err);
        setStatus("error");
        return null;
      }
      setStatus("signing");
      try {
        const txHash = call.data ? await walletClient.sendTransaction({
          to: call.address,
          data: call.data,
          account: walletClient.account,
          chain: walletClient.chain,
          ...value != null ? { value } : {}
        }) : await walletClient.writeContract({
          address: call.address,
          abi: call.abi,
          functionName: call.functionName,
          args: call.args,
          account: walletClient.account,
          chain: walletClient.chain,
          ...value != null ? { value } : {}
        });
        submittedHash = txHash;
        if (current()) {
          setHash(txHash);
          setStatus("pending");
        }
        if (!publicClient) {
          if (current()) setStatus(writeOutcome(null));
          return txHash;
        }
        let replacementReason;
        const r = await publicClient.waitForTransactionReceipt({
          hash: txHash,
          onReplaced: ({ reason }) => {
            replacementReason = reason;
          }
        });
        submittedHash = r.transactionHash;
        if (!current()) return submittedHash;
        setHash(submittedHash);
        setReceipt(r);
        if (replacementReason === "cancelled" || replacementReason === "replaced") {
          setError(new Error(replacementReason === "cancelled" ? "Transaction was cancelled in the wallet." : "Transaction was replaced by a different transaction; the requested call was not confirmed."));
          setStatus("error");
          return submittedHash;
        }
        if (writeOutcome(r) === "reverted") {
          log4.warn("contract write reverted on chain", {
            address: call.address,
            functionName: call.functionName,
            hash: txHash
          });
          setError(
            new Error(
              `Transaction reverted on chain. ${call.functionName} was mined but the contract rejected it, so nothing changed.`
            )
          );
          setStatus("reverted");
          return submittedHash;
        }
        setStatus(writeOutcome(r));
        return submittedHash;
      } catch (err) {
        log4.warn("contract write failed", { address: call.address, functionName: call.functionName, error: String(err) });
        if (current()) {
          setError(err instanceof Error ? err : new Error(String(err)));
          setStatus("error");
        }
        return submittedHash;
      }
    },
    [walletClient, walletError, publicClient]
  );
  return { write, hash, receipt, status, error, reset };
}
var log5 = createLogger(`${WEB3_PACKAGE}:storage`);
function useEvmStorageAt(params) {
  const { address, slot, chainId, blockNumber, blockTag, enabled = true } = params;
  return useEvmRead({
    chainId,
    blockNumber,
    blockTag,
    ready: enabled && Boolean(address && slot),
    deps: [address, slot],
    read: async (client, block) => await client.getStorageAt({
      address,
      slot,
      ...block
    }),
    onFailure: { log: log5, message: "storage read failed", context: () => ({ address, slot }) }
  });
}
var log6 = createLogger(`${WEB3_PACKAGE}:signer`);
function useAutheSigner(options = {}) {
  const { rootKeyId = WEB3_SIGNER_ROOT_ID, ttlMs = WEB3_SIGNER_TTL_MS, enabled = true } = options;
  const request = React10.useMemo(() => ({ enabled, rootKeyId, ttlMs }), [enabled, rootKeyId, ttlMs]);
  const [state, setState] = React10.useState(null);
  const current = enabled && state?.request === request ? state : null;
  const signer = current?.signer ?? null;
  const error = current?.error ?? null;
  React10.useEffect(() => {
    if (!enabled) return;
    let active = true;
    void TwoKeySigner.create({ rootKeyId, intermediateTtlMs: ttlMs }).then((created) => {
      if (active) setState({ request, signer: created, error: null });
    }).catch((err) => {
      if (!active) return;
      log6.error("two-key signer creation failed", { error: String(err) });
      setState({ request, signer: null, error: err instanceof Error ? err : new Error(String(err)) });
    });
    return () => {
      active = false;
    };
  }, [enabled, rootKeyId, ttlMs, request]);
  const notReady = React10.useCallback(
    () => Promise.reject(
      error ? new Error(`Authe signer is unavailable: ${error.message}`) : new Error("Authe signer is not ready yet.")
    ),
    [error]
  );
  const signRequest = React10.useCallback(
    (payload) => signer ? signer.signRequest(payload) : notReady(),
    [signer, notReady]
  );
  const exportRootPublicJwk = React10.useCallback(
    () => signer ? signer.exportRootPublicJwk() : notReady(),
    [signer, notReady]
  );
  return { signer, ready: signer !== null, error, signRequest, exportRootPublicJwk };
}
function useEvmSignMessage() {
  const { signMessageAsync, isPending, error } = useSignMessage();
  const { address: account } = useAccount();
  const publicClient = usePublicClient();
  const signMessage = React10.useCallback(
    (message) => signMessageAsync({ message }),
    [signMessageAsync]
  );
  const signAndRecover = React10.useCallback(
    async (message) => {
      const signature = await signMessageAsync({ message });
      const signer = await recoverMessageAddress({ message, signature });
      return {
        signature,
        signer,
        matchesAccount: account ? isAddressEqual(signer, account) : null
      };
    },
    [signMessageAsync, account]
  );
  const signAndVerify = React10.useCallback(
    async (message) => {
      const signature = await signMessageAsync({ message });
      if (!account) {
        return { signature, verdict: { valid: false, method: "unknown", reason: "needs-client" } };
      }
      const verdict = await verifyEvmSignature(account, message, signature, publicClient);
      return { signature, verdict };
    },
    [signMessageAsync, account, publicClient]
  );
  return {
    signMessage,
    signAndRecover,
    signAndVerify,
    isPending,
    error: error ?? null
  };
}
function recoverSigner(message, signature) {
  return recoverMessageAddress({ message, signature });
}
async function verifyEvmSignature(address, message, signature, client) {
  if (client) {
    const valid2 = await verifyMessage(client, { address, message, signature });
    return { valid: valid2, method: "erc1271" };
  }
  const valid = await verifyMessage$1({ address, message, signature });
  if (valid) return { valid: true, method: "eoa" };
  return { valid: false, method: "unknown", reason: "needs-client" };
}
var ContentContext = React10.createContext(null);
function Content() {
  return React10.useContext(ContentContext);
}
var WalletBoundary = React10.memo(function WalletBoundary2({ config, reconnectOnMount }) {
  return /* @__PURE__ */ jsx(WagmiProvider, { config, reconnectOnMount, children: /* @__PURE__ */ jsx(Content, {}) });
});
function Web3Provider({
  config,
  queryClient,
  reconnectOnMount,
  children
}) {
  const client = React10.useMemo(() => queryClient ?? new QueryClient(), [queryClient]);
  const [resolvedReconnect] = React10.useState(
    () => reconnectOnMount ?? !isDisconnected(WEB3_EVM_CONNECTION_ID)
  );
  return /* @__PURE__ */ jsx(QueryClientProvider, { client, children: /* @__PURE__ */ jsx(ContentContext.Provider, { value: children, children: /* @__PURE__ */ jsx(WalletBoundary, { config, reconnectOnMount: resolvedReconnect }) }) });
}
function persistSlug(slug) {
  if (slug) writeLocalJson(WEB3_SELECTED_CHAIN_KEY, slug);
}
function useSelectedChain() {
  const configuredChainId = useChainId();
  const account = useAccount();
  const chains = useChains();
  const chainId = account.isConnected && account.chainId != null ? account.chainId : configuredChainId;
  const { switchChain, isPending, error } = useSwitchChain();
  const chain = getChainMetadata(String(chainId));
  const requestSwitch = React10.useCallback(
    (id, slug) => {
      switchChain({ chainId: id }, { onSuccess: () => persistSlug(slug) });
    },
    [switchChain]
  );
  const switchToChainId = React10.useCallback(
    (id) => requestSwitch(id, getChainMetadata(String(id))?.slug),
    [requestSwitch]
  );
  const switchToSlug = React10.useCallback(
    (slug) => {
      const hex = toHexChainId(slug);
      if (!hex) return false;
      const id = Number(BigInt(hex));
      if (!Number.isSafeInteger(id) || !chains.some((configured) => configured.id === id)) return false;
      requestSwitch(id, getChainMetadata(slug)?.slug);
      return true;
    },
    [requestSwitch, chains]
  );
  return {
    chainId,
    chain,
    isSupported: chains.some((configured) => configured.id === chainId),
    isSwitching: isPending,
    switchError: error ?? null,
    switchToChainId,
    switchToSlug
  };
}
function useAddressInventory() {
  const { address, addresses, chainId, isConnected, status, connector } = useAccount();
  const entries = React10.useMemo(() => {
    if (!isConnected) return [];
    const list = addresses ?? (address ? [address] : []);
    const activeLower = address?.toLowerCase();
    return list.map((a) => ({
      address: a,
      normalized: normalizeAddress(a),
      isActive: a.toLowerCase() === activeLower
    }));
  }, [addresses, address, isConnected]);
  const active = React10.useMemo(() => entries.find((e) => e.isActive) ?? null, [entries]);
  const chain = chainId != null ? getChainMetadata(String(chainId)) : null;
  return { entries, active, chainId, chain, isConnected, status, connector: connector?.name };
}

// src/wallet/connect-recovery.ts
function watchStuckWalletConnect(onStuck) {
  if (typeof document === "undefined" || typeof window === "undefined") return () => void 0;
  let timer;
  const schedule = () => {
    if (timer !== void 0) clearTimeout(timer);
    timer = setTimeout(onStuck, 400);
  };
  const onVisibility = () => {
    if (document.visibilityState === "visible") schedule();
  };
  document.addEventListener("visibilitychange", onVisibility);
  window.addEventListener("focus", schedule);
  return () => {
    if (timer !== void 0) clearTimeout(timer);
    document.removeEventListener("visibilitychange", onVisibility);
    window.removeEventListener("focus", schedule);
  };
}

// src/wallet/connection-error.ts
function walletConnectionError(error, wallet = "your wallet") {
  let cause = error;
  for (let depth = 0; cause && typeof cause === "object" && depth < 8; depth += 1) {
    const rpc = cause;
    if (rpc.code === 4001) return new Error("Connection request rejected. Click Connect to try again.");
    if (rpc.code === -32002) return new Error(`A connection request is already open in ${wallet}. Open the extension to approve or reject it, then click Connect again.`);
    cause = rpc.cause;
  }
  return error instanceof Error ? error : new Error("Connection failed. Please try again.");
}

// src/react/useWalletConnection.ts
function useWalletConnection() {
  const { address, isConnected } = useAccount();
  const { connectAsync, connectors: registered, reset } = useConnect();
  const connectors = React10.useMemo(() => registered.some((connector) => connector.type === "injected" && connector.id !== "injected") ? registered.filter((connector) => connector.id !== "injected") : registered, [registered]);
  const { disconnect } = useDisconnect();
  const [isConnecting, setIsConnecting] = React10.useState(false);
  const [error, setError] = React10.useState(null);
  const [selectedConnector, setSelectedConnector] = React10.useState();
  const attemptRef = React10.useRef(0);
  React10.useEffect(() => () => {
    attemptRef.current += 1;
  }, []);
  const clearConnecting = React10.useCallback(() => {
    setIsConnecting(false);
    reset();
  }, [reset]);
  const doConnect = React10.useCallback(
    (connector) => {
      const target = connector ?? (connectors.length === 1 ? connectors[0] : void 0);
      if (!connector && connectors.length > 1) {
        setError(new Error("Choose which wallet to connect."));
        return;
      }
      if (!target) {
        setError(new Error("No wallet connector is available."));
        return;
      }
      setSelectedConnector(target);
      const attempt = ++attemptRef.current;
      reset();
      clearDisconnected(WEB3_EVM_CONNECTION_ID);
      setIsConnecting(true);
      setError(null);
      void (async () => {
        try {
          await connectAsync({ connector: target });
          if (attempt !== attemptRef.current && isDisconnected(WEB3_EVM_CONNECTION_ID)) {
            disconnect({ connector: target });
          }
        } catch (e) {
          if (attempt !== attemptRef.current) return;
          setError(walletConnectionError(e, target.name));
          reset();
        } finally {
          if (attempt === attemptRef.current) setIsConnecting(false);
        }
      })();
    },
    [connectAsync, connectors, reset, disconnect]
  );
  React10.useEffect(() => {
    if (!isConnecting) return;
    return watchStuckWalletConnect(() => {
      if (isConnected) return;
      setError((prev) => prev ?? new Error("Connection cancelled"));
      attemptRef.current += 1;
      clearConnecting();
    });
  }, [isConnecting, isConnected, clearConnecting]);
  const doDisconnect = React10.useCallback(() => {
    attemptRef.current += 1;
    clearConnecting();
    setError(null);
    markDisconnected(WEB3_EVM_CONNECTION_ID);
    disconnect();
  }, [disconnect, clearConnecting]);
  return {
    isConnected,
    address,
    connectors,
    connect: doConnect,
    disconnect: doDisconnect,
    isConnecting,
    error,
    selectedConnector
  };
}

// src/wallet/injected.ts
function injected2() {
  return (typeof window !== "undefined" ? window : {}) ?? {};
}
var getEvmProvider = () => injected2().ethereum;
var getSolanaProvider = () => injected2().solana;
var getBitcoinProvider = () => injected2().unisat;

// src/wallet/adapters.ts
function createInjectedEvmAdapter(options = {}) {
  const getProvider = options.getProvider ?? getEvmProvider;
  const toAccounts = (addrs, chainId) => {
    const chain = chainId ? getChainMetadata(chainId)?.slug ?? chainId : void 0;
    return addrs.map((a) => ({ address: a.toLowerCase(), family: ChainFamily.EVM, chain }));
  };
  return {
    id: options.id ?? "injected-evm",
    name: options.name ?? "Browser wallet",
    family: ChainFamily.EVM,
    isAvailable: () => Boolean(getProvider()),
    async connect() {
      const provider = getProvider();
      if (!provider) throw new Error("No EVM wallet found");
      const addrs = await provider.request({ method: "eth_requestAccounts" });
      const chainId = await provider.request({ method: "eth_chainId" }).catch(() => void 0);
      return toAccounts(addrs ?? [], chainId);
    },
    async getAccounts() {
      const provider = getProvider();
      if (!provider) return [];
      const addrs = await provider.request({ method: "eth_accounts" }).catch(() => []);
      const chainId = await provider.request({ method: "eth_chainId" }).catch(() => void 0);
      return toAccounts(addrs ?? [], typeof chainId === "string" ? chainId : void 0);
    },
    async disconnect() {
    },
    subscribe(listener) {
      const provider = getProvider();
      if (!provider?.on || !provider.removeListener) return () => void 0;
      let revision = 0;
      const refresh = async (addrs, chainId) => {
        const request = ++revision;
        const [accounts, chain] = await Promise.all([
          addrs ?? provider.request({ method: "eth_accounts" }).catch(() => []),
          chainId ?? provider.request({ method: "eth_chainId" }).catch(() => void 0)
        ]);
        if (request === revision) listener(toAccounts(accounts, typeof chain === "string" ? chain : void 0));
      };
      const onAccounts = (addrs) => {
        void refresh(addrs);
      };
      const onChain = (chainId) => {
        void refresh(void 0, chainId);
      };
      const onDisconnect = () => {
        revision += 1;
        listener([]);
      };
      provider.on("accountsChanged", onAccounts);
      provider.on("chainChanged", onChain);
      provider.on("disconnect", onDisconnect);
      return () => {
        revision += 1;
        provider.removeListener?.("accountsChanged", onAccounts);
        provider.removeListener?.("chainChanged", onChain);
        provider.removeListener?.("disconnect", onDisconnect);
      };
    }
  };
}
function createSolanaAdapter(options = {}) {
  const getProvider = options.getProvider ?? getSolanaProvider;
  const account = (address) => ({ address, family: ChainFamily.SOLANA, chain: "solana" });
  return {
    id: options.id ?? "phantom",
    name: options.name ?? "Phantom",
    family: ChainFamily.SOLANA,
    isAvailable: () => Boolean(getProvider()),
    async connect() {
      const provider = getProvider();
      if (!provider) throw new Error("No Solana wallet found");
      const res = await provider.connect();
      return [account(res.publicKey.toString())];
    },
    async getAccounts() {
      const pk = getProvider()?.publicKey;
      return pk ? [account(pk.toString())] : [];
    },
    async disconnect() {
      await getProvider()?.disconnect();
    },
    subscribe(listener) {
      const provider = getProvider();
      if (!provider?.on || !provider.removeListener) return () => void 0;
      const onAccount = (key) => listener(key ? [account(key.toString())] : []);
      const onConnect = () => onAccount(provider.publicKey ?? null);
      const onDisconnect = () => listener([]);
      provider.on("accountChanged", onAccount);
      provider.on("connect", onConnect);
      provider.on("disconnect", onDisconnect);
      return () => {
        provider.removeListener?.("accountChanged", onAccount);
        provider.removeListener?.("connect", onConnect);
        provider.removeListener?.("disconnect", onDisconnect);
      };
    }
  };
}
function createBitcoinAdapter(options = {}) {
  const getProvider = options.getProvider ?? getBitcoinProvider;
  const toAccounts = (addrs) => (addrs ?? []).map((a) => ({ address: a, family: ChainFamily.UTXO, chain: "bitcoin" }));
  return {
    id: options.id ?? "unisat",
    name: options.name ?? "Unisat",
    family: ChainFamily.UTXO,
    isAvailable: () => Boolean(getProvider()),
    async connect() {
      const provider = getProvider();
      if (!provider) throw new Error("No Bitcoin wallet found");
      return toAccounts(await provider.requestAccounts());
    },
    async getAccounts() {
      const provider = getProvider();
      if (!provider) return [];
      return toAccounts(await provider.getAccounts().catch(() => []));
    },
    async disconnect() {
    },
    subscribe(listener) {
      const provider = getProvider();
      if (!provider?.on || !provider.removeListener) return () => void 0;
      const onAccounts = (addrs) => listener(toAccounts(addrs));
      provider.on("accountsChanged", onAccounts);
      return () => provider.removeListener?.("accountsChanged", onAccounts);
    }
  };
}
function defaultWalletAdapters() {
  return [createInjectedEvmAdapter(), createSolanaAdapter(), createBitcoinAdapter()];
}
var WalletsContext = React10.createContext(null);
function WalletProvider({ adapters, autoConnect = true, children }) {
  const adapterList = React10.useMemo(() => adapters ?? defaultWalletAdapters(), [adapters]);
  const value = useWalletHubState(adapterList, autoConnect);
  return /* @__PURE__ */ jsx(WalletsContext.Provider, { value, children });
}
function useWallets() {
  const ctx = React10.useContext(WalletsContext);
  if (!ctx) throw new Error("useWallets must be used within a <WalletProvider>");
  return ctx;
}
function useWalletHubState(adapters, autoConnect) {
  const [snapshot, setSnapshot] = React10.useState(/* @__PURE__ */ new Map());
  const operations = React10.useRef(/* @__PURE__ */ new Map());
  const watchers = React10.useRef(/* @__PURE__ */ new Map());
  const listening = React10.useRef(/* @__PURE__ */ new Set());
  const liveAdapters = React10.useRef(/* @__PURE__ */ new Map());
  const byId = React10.useMemo(() => new Map(adapters.map((a) => [a.id, a])), [adapters]);
  const resolve = React10.useCallback(
    (target) => {
      const adapter = byId.get(typeof target === "string" ? target : target.id);
      return typeof target === "string" || adapter === target ? adapter : void 0;
    },
    [byId]
  );
  const begin = React10.useCallback((adapter) => {
    watchers.current.get(adapter)?.();
    watchers.current.delete(adapter);
    const operation = {};
    operations.current.set(adapter, operation);
    return () => liveAdapters.current.has(adapter) && operations.current.get(adapter) === operation;
  }, []);
  const update = React10.useCallback((adapter, state) => {
    setSnapshot((prev) => new Map(prev).set(adapter, state));
  }, []);
  const applyAccounts = React10.useCallback((adapter, accounts) => {
    update(adapter, { accounts, status: accounts.length ? "connected" : "disconnected" });
  }, [update]);
  const remove = React10.useCallback((adapter) => {
    liveAdapters.current.get(adapter)?.unsubscribe?.();
    liveAdapters.current.delete(adapter);
    operations.current.delete(adapter);
    listening.current.delete(adapter);
    watchers.current.get(adapter)?.();
    watchers.current.delete(adapter);
  }, []);
  React10.useEffect(() => {
    const live = liveAdapters.current;
    return () => {
      for (const adapter of live.keys()) remove(adapter);
    };
  }, [remove]);
  React10.useEffect(() => {
    const next = new Set(adapters);
    const removed = [...liveAdapters.current.keys()].filter((adapter) => !next.has(adapter));
    for (const adapter of removed) remove(adapter);
    if (removed.length) setSnapshot((prev) => {
      const states = new Map(prev);
      for (const adapter of removed) states.delete(adapter);
      return states;
    });
    for (const adapter of adapters) {
      if (!adapter.isAvailable()) continue;
      let entry = liveAdapters.current.get(adapter);
      if (!entry) {
        entry = { hydrated: false };
        liveAdapters.current.set(adapter, entry);
        entry.unsubscribe = adapter.subscribe?.((accounts) => {
          if (!liveAdapters.current.has(adapter) || !listening.current.has(adapter)) return;
          begin(adapter);
          applyAccounts(adapter, accounts);
        });
      }
      if (autoConnect && !entry.hydrated && !listening.current.has(adapter) && !isDisconnected(adapter.id)) {
        entry.hydrated = true;
        listening.current.add(adapter);
        const current = begin(adapter);
        void adapter.getAccounts().then((accounts) => {
          if (current() && listening.current.has(adapter)) applyAccounts(adapter, accounts);
        }).catch(() => void 0);
      }
    }
  }, [adapters, autoConnect, applyAccounts, begin, remove]);
  const connect = React10.useCallback(async (target) => {
    const adapter = resolve(target);
    if (!adapter || !liveAdapters.current.has(adapter)) return;
    liveAdapters.current.get(adapter).hydrated = true;
    const current = begin(adapter);
    listening.current.add(adapter);
    clearDisconnected(adapter.id);
    update(adapter, { accounts: [], status: "connecting" });
    const stopWatch = watchStuckWalletConnect(() => {
      if (!current()) return;
      begin(adapter);
      listening.current.delete(adapter);
      update(adapter, { accounts: [], status: "disconnected", error: "Connection cancelled" });
    });
    watchers.current.set(adapter, stopWatch);
    try {
      const accounts = await adapter.connect();
      if (current()) applyAccounts(adapter, accounts);
    } catch (error) {
      if (current()) {
        listening.current.delete(adapter);
        update(adapter, { accounts: [], status: "error", error: walletConnectionError(error, adapter.name).message });
      }
    } finally {
      stopWatch();
      if (current()) watchers.current.delete(adapter);
    }
  }, [resolve, begin, update, applyAccounts]);
  const disconnect = React10.useCallback(async (target) => {
    const adapter = resolve(target);
    if (!adapter || !liveAdapters.current.has(adapter)) return;
    const current = begin(adapter);
    listening.current.delete(adapter);
    markDisconnected(adapter.id);
    applyAccounts(adapter, []);
    try {
      await adapter.disconnect();
    } catch (error) {
      if (current()) update(adapter, {
        accounts: [],
        status: "disconnected",
        error: error instanceof Error ? error.message : "Disconnect failed"
      });
    }
  }, [resolve, begin, applyAccounts, update]);
  return React10.useMemo(() => {
    const states = new Map(adapters.map((adapter) => [adapter.id, snapshot.get(adapter)]));
    const accountsByAdapter = {};
    const accountsByFamily = {};
    for (const adapter of adapters) {
      const accounts2 = states.get(adapter.id)?.accounts ?? [];
      accountsByAdapter[adapter.id] = accounts2;
      (accountsByFamily[adapter.family] ??= []).push(...accounts2);
    }
    const accounts = Object.values(accountsByAdapter).flat();
    return {
      adapters,
      available: adapters.filter((adapter) => adapter.isAvailable()),
      accounts,
      accountsByAdapter,
      accountsByFamily,
      status: (id) => states.get(id)?.status ?? "disconnected",
      error: (id) => states.get(id)?.error,
      connect,
      disconnect,
      isConnected: (family) => family ? (accountsByFamily[family]?.length ?? 0) > 0 : accounts.length > 0
    };
  }, [adapters, snapshot, connect, disconnect]);
}
function ChainPicker({
  slugs,
  includeTestnets = false,
  placeholder = "Select chain",
  className
}) {
  const { chain, chainId, switchToSlug, switchError, isSwitching } = useSelectedChain();
  const chains = useChains();
  const errorId = React10.useId();
  const registryVersion = useChainRegistryVersion();
  const options = React10.useMemo(() => {
    const wanted = slugs ? new Set(slugs) : null;
    const configured = new Set(chains.map((item) => getChainMetadata(String(item.id))?.slug));
    const all = chainSelectOptions({ valueKind: "slug", evmOnly: true, includeTestnets }).filter((option) => configured.has(option.slug));
    return wanted ? all.filter((option) => wanted.has(option.slug)) : all;
  }, [slugs, includeTestnets, chains, registryVersion]);
  const selection = options.find((option) => option.slug === chain?.slug);
  const currentName = chain?.name ?? chains.find((configured) => configured.id === chainId)?.name ?? `Unsupported network (${chainId})`;
  return /* @__PURE__ */ jsxs("div", { className: "flex flex-col gap-1", children: [
    /* @__PURE__ */ jsxs(
      Select,
      {
        value: selection?.slug ?? "",
        onValueChange: (slug) => switchToSlug(slug),
        disabled: isSwitching || options.length === 0,
        children: [
          /* @__PURE__ */ jsx(
            SelectTrigger,
            {
              className: cn("w-[200px]", className),
              "aria-label": "Wallet network",
              "aria-describedby": switchError ? errorId : void 0,
              "aria-invalid": switchError ? true : void 0,
              children: /* @__PURE__ */ jsx(SelectValue, { placeholder: selection ? placeholder : currentName })
            }
          ),
          /* @__PURE__ */ jsx(SelectContent, { children: options.map((option) => /* @__PURE__ */ jsx(SelectItem, { value: option.value, children: /* @__PURE__ */ jsx(ChainAvatar, { chain: option.slug, size: "sm" }) }, option.value)) })
        ]
      }
    ),
    switchError && /* @__PURE__ */ jsxs("p", { id: errorId, role: "alert", className: "max-w-[200px] text-2xs text-destructive", children: [
      "Could not switch chain: ",
      switchError.message
    ] })
  ] });
}
var FAMILY_LABEL = {
  evm: "EVM",
  utxo: "Bitcoin",
  solana: "Solana",
  cosmos: "Cosmos",
  xrpl: "XRP Ledger",
  tron: "TRON",
  ton: "TON",
  near: "NEAR",
  aptos: "Aptos",
  sui: "Sui",
  starknet: "Starknet",
  antelope: "Antelope",
  hypercore: "Hyperliquid",
  corda: "Corda",
  fabric: "Hyperledger Fabric",
  canton: "Canton",
  other: "Other"
};
function WalletMenu({ onlyAvailable = false, className }) {
  const { adapters, accountsByAdapter, status, error, connect, disconnect } = useWallets();
  const rows = onlyAvailable ? adapters.filter((a) => a.isAvailable()) : adapters;
  return /* @__PURE__ */ jsx("div", { className: cn("divide-y rounded-md border", className), children: rows.map((adapter) => {
    const available = adapter.isAvailable();
    const account = accountsByAdapter[adapter.id]?.[0];
    const state = status(adapter.id);
    const err = error(adapter.id);
    const connected = state === "connected" && account;
    return /* @__PURE__ */ jsxs("div", { className: "flex items-center gap-3 px-3 py-2.5", children: [
      /* @__PURE__ */ jsxs("div", { className: "min-w-0 flex-1", children: [
        /* @__PURE__ */ jsxs("div", { className: "flex items-center gap-2", children: [
          /* @__PURE__ */ jsx("span", { className: "text-sm font-medium", children: adapter.name }),
          /* @__PURE__ */ jsx(Badge, { variant: "outline", className: "text-3xs", children: FAMILY_LABEL[adapter.family] })
        ] }),
        connected ? /* @__PURE__ */ jsx("div", { className: "font-mono text-xs text-muted-foreground", children: /* @__PURE__ */ jsx(AddressDisplay, { address: account.address, chain: account.chain }) }) : err ? /* @__PURE__ */ jsx("p", { role: "alert", className: "break-words text-xs text-destructive", children: err }) : !available ? /* @__PURE__ */ jsx("p", { className: "text-xs text-muted-foreground", children: "Not installed" }) : null
      ] }),
      connected ? /* @__PURE__ */ jsx(Button, { type: "button", size: "sm", variant: "outline", "aria-label": `Disconnect ${adapter.name}`, onClick: () => void disconnect(adapter), children: "Disconnect" }) : /* @__PURE__ */ jsx(
        Button,
        {
          type: "button",
          size: "sm",
          "aria-label": `Connect ${adapter.name}`,
          disabled: !available,
          onClick: () => void connect(adapter),
          children: state === "connecting" ? "Connecting\u2026" : "Connect"
        }
      )
    ] }, adapter.id);
  }) });
}
function EvmWalletMenu({ className }) {
  const { connectors, connect, disconnect, isConnected, isConnecting, selectedConnector, error } = useWalletConnection();
  const [detected, setDetected] = React10.useState();
  React10.useEffect(() => {
    let current = true;
    void Promise.all(connectors.map(async (connector) => {
      const available = connector.type !== "injected" || Boolean(await connector.getProvider().catch(() => void 0));
      return available ? connector.uid : void 0;
    })).then((ids) => {
      if (current) setDetected({ connectors, ids: new Set(ids.filter((id) => id !== void 0)) });
    });
    return () => {
      current = false;
    };
  }, [connectors]);
  const ready = detected?.connectors === connectors;
  const wallets = connectors.filter((connector) => detected?.ids.has(connector.uid));
  return /* @__PURE__ */ jsxs("div", { className: cn("space-y-3", className), children: [
    isConnected ? /* @__PURE__ */ jsx(Button, { type: "button", size: "sm", variant: "outline", onClick: disconnect, children: "Disconnect EVM wallet" }) : !ready ? /* @__PURE__ */ jsx("p", { role: "status", className: "text-sm text-muted-foreground", children: "Checking installed wallets\u2026" }) : wallets.length ? /* @__PURE__ */ jsx("ul", { className: "divide-y rounded-md border", "aria-label": "EVM wallets", children: wallets.map((connector) => /* @__PURE__ */ jsxs("li", { className: "flex items-center justify-between gap-3 px-3 py-2.5", children: [
      /* @__PURE__ */ jsx("span", { className: "text-sm font-medium", children: connector.name }),
      /* @__PURE__ */ jsx(Button, { type: "button", size: "sm", "aria-label": `Connect ${connector.name}`, onClick: () => connect(connector), children: isConnecting && selectedConnector?.uid === connector.uid ? "Retry connection" : "Connect" })
    ] }, connector.uid)) }) : /* @__PURE__ */ jsx("p", { className: "text-sm text-muted-foreground", children: "No EVM wallet detected. Enable MetaMask or another EVM wallet extension in this browser, then reload this page." }),
    isConnecting && /* @__PURE__ */ jsxs("p", { role: "status", className: "text-sm text-muted-foreground", children: [
      "Open ",
      selectedConnector?.name ?? "your wallet",
      " to approve or reject the connection request."
    ] }),
    error && /* @__PURE__ */ jsx("p", { role: "alert", className: "break-words text-sm text-destructive", children: error.message })
  ] });
}

export { ChainPicker, EvmWalletMenu, WEB3_DEFAULT_CHAIN_SLUG, WEB3_EVM_CONNECTION_ID, WEB3_PACKAGE, WEB3_SELECTED_CHAIN_KEY, WEB3_SIGNER_ROOT_ID, WEB3_SIGNER_TTL_MS, WalletMenu, WalletProvider, Web3Provider, autheEvmViemChains, blockOverride, clearDisconnected, createAutheWagmiConfig, createBitcoinAdapter, createInjectedEvmAdapter, createRpcContractReader, createSolanaAdapter, defaultWalletAdapters, isDisconnected, markDisconnected, recoverSigner, resolveRpcUrls, rpcTransport, stableArgsKey, toViemChain, useAddressInventory, useAutheSigner, useContractRead, useContractReads, useContractWrite, useEvmRead, useEvmSignMessage, useEvmStorageAt, useSelectedChain, useWalletConnection, useWallets, verifyEvmSignature, writeOutcome };
//# sourceMappingURL=index.js.map
//# sourceMappingURL=index.js.map